import React, { useState } from 'react';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import { CurrencyType } from 'types/Currency';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { BookingStatus, Price as BookingPrice } from '@daedalus/core/src/api-types/bovio/response/booking';
import { getIsPayAtPropertyMoR } from '@daedalus/core/src/offer/business/getIsPayAtPropertyMoR';
import { OfferPrice } from '@daedalus/core/src/offer/types/offer';
import { ProviderTraderInformation } from '@daedalus/core/src/offer/types/ProviderTraderInformation';
import { CurrencyDisplayVariant } from '@daedalus/core/src/price/business/formatPrice';
import { getPriceDetails } from '@daedalus/core/src/price/business/price';
import { calculateDateDifference } from '@daedalus/core/src/utils/date';
import { LanguageCurrencySelector } from '../../search/LanguageCurrencySelector';
import { getTotalChargeLaterDifferentialPrice } from '../OfferDetails/utils';
import Disclaimers from './Disclaimers';
import PriceBreakdown from './PriceBreakdown';
import PriceTotal from './PriceTotal';
import { PaymentInformationSkeleton } from './Skeleton';
interface PriceDetailsProps {
  /** The Check in date of the offer */
  checkIn: string;
  /** The Check out date of the offer */
  checkOut: string;
  /** Merchant of record */
  merchantOfRecord?: string;
  /** Number of rooms */
  numberOfRooms: number;
  /** Prices */
  prices: OfferPrice[] | BookingPrice[];
  /** Total number of guests */
  numberOfGuests: number;
  /** Is loading status flag */
  isLoading?: boolean;
  /** Whether to display the disclaimer text */
  showDisclaimerText?: boolean;
  /** Whether to display the fees popups */
  showFeesDescriptions?: boolean;
  /** Whether to show the currency selector */
  showCurrencySelector?: boolean;
  /** Information about the trader and its terms and conditions */
  providerTraderInformation?: ProviderTraderInformation;
  /** List of enabled currencies */
  enabledCurrencies?: CurrencyType[];
  /**
    The status of the booking (confirmed, cancelled, frozen, freeze_cancelled, freeze_expired)
   */
  status?: BookingStatus;
  /**
   * Whether to display the number of rooms in the PriceTotal component
   */
  displayNumberOfRooms?: boolean;
  /**
   * Extra actions to perform on FeesDetails click
   */
  onFeesDetailsClick?: () => void;
  /**
   * Extra actions to perform on Currency change
   */
  onCurrencyChange?: (currency: string) => void;
  /**
   * Extra actions to perform on Currency click
   */
  onCurrencyClick?: () => void;
  /** The currency the hotel will charge the hotelfees */
  propertyCurrencyCode: string | undefined;

  /** How to display the currency in currency formatting. i.e. 'symbol', 'narrowSymbol', 'code', 'name', */
  currencyDisplayVariant?: CurrencyDisplayVariant;
  isChargeLaterSelected?: boolean;
  /** Whether to display the discreet list of transparent fees */
  displayTransparentFees?: boolean;

  /** Whether the booking is a bundle booking */
  isSplitBooking?: boolean;
}
export const NUMBER_OF_DECIMALS_FOR_PRICE = 2;

// If 0533f9e8-transparent-taxes-and-fees-v1 B side wins, we can remove this enum in favor of bovio generated breakdown type
export enum FeeTypes {
  TaxesFees = 'taxes_fees',
  LocalTax = 'local_tax',
  ResortFee = 'resort_fee',
  OtherFee = 'other_fee',
  PayAtProperty = 'pay_at_property',
  ToCompleteTheBooking = 'to_complete_the_booking',
  CityTax = 'city_tax',
  ServiceCharge = 'service_charge',
  CleaningFee = 'cleaning_fee',
  DestinationFee = 'destination_fee',
  EnvironmentFee = 'environment_fee',
  GoodsAndServicesTax = 'goods_and_services_tax',
  GovernmentTax = 'government_tax',
  MunicipalityFee = 'municipality_fee',
  Other = 'other',
  TourismFee = 'tourism_fee',
  Tax = 'tax',
  Vat = 'vat',
}
const PriceBreakdownWrapper = /*#__PURE__*/styled('div')({
  name: "PriceBreakdownWrapper",
  class: "p1xrwy2",
  propsAsIs: false
});
export const PriceDetails = ({
  checkIn,
  checkOut,
  numberOfGuests,
  numberOfRooms,
  merchantOfRecord,
  prices,
  showDisclaimerText = false,
  isLoading = false,
  showFeesDescriptions = true,
  showCurrencySelector = true,
  enabledCurrencies,
  displayNumberOfRooms = true,
  onFeesDetailsClick,
  onCurrencyClick,
  onCurrencyChange,
  propertyCurrencyCode,
  currencyDisplayVariant,
  isChargeLaterSelected,
  displayTransparentFees = false,
  isSplitBooking = false
}: PriceDetailsProps) => {
  const [currencySelectorModalOpen, setCurrencySelectorModalOpen] = useState(false);
  const {
    isMobile
  } = useDeviceLayout();
  const isPayAtProperty = getIsPayAtPropertyMoR({
    merchantOfRecord
  });
  if (isLoading) return <PaymentInformationSkeleton />;
  const {
    totalDisplayPrice,
    displayCurrencyCode,
    chargeableCurrencyCode,
    totalAtPropertyDisplayPrice,
    totalUpFrontChargeablePrice,
    totalUpFrontDisplayPrice
  } = getPriceDetails(prices, isPayAtProperty);
  const numberOfNights = calculateDateDifference(new Date(checkIn), new Date(checkOut));
  const withCurrencyConversion = displayCurrencyCode && chargeableCurrencyCode && displayCurrencyCode !== chargeableCurrencyCode && totalUpFrontChargeablePrice && totalUpFrontChargeablePrice > 0;
  const withPropertyCurrencyConversion = displayCurrencyCode && propertyCurrencyCode && displayCurrencyCode !== propertyCurrencyCode && totalAtPropertyDisplayPrice && totalAtPropertyDisplayPrice > 0;
  const handleCurrencyClick = () => {
    onCurrencyClick?.();
    setCurrencySelectorModalOpen(!currencySelectorModalOpen);
  };
  const handleCurrencyChange = (currency: string) => {
    onCurrencyChange?.(currency);
    setCurrencySelectorModalOpen(false);
  };
  return <>
      <PriceBreakdownWrapper>
        <PriceBreakdown showFeesDescriptions={showFeesDescriptions} isPriceLoading={isLoading} propertyCurrencyCode={propertyCurrencyCode} isChargeLaterSelected={isChargeLaterSelected} onFeesDetailsClick={onFeesDetailsClick} currencyDisplayVariant={currencyDisplayVariant} prices={prices} isPayAtProperty={isPayAtProperty} displayTransparentFees={displayTransparentFees} numberOfRooms={numberOfRooms} numberOfNights={numberOfNights} isSplitBooking={isSplitBooking} />

        <PriceTotal showCurrencySelector={showCurrencySelector} isPriceLoading={isLoading} currencyCode={String(displayCurrencyCode)} totalPrice={Number(toggle('50feb427-cl-differential-price', totalDisplayPrice, isChargeLaterSelected ? getTotalChargeLaterDifferentialPrice(totalDisplayPrice) : totalDisplayPrice))} rooms={numberOfRooms} nights={numberOfNights} guests={numberOfGuests} onCurrencyClick={handleCurrencyClick} displayNumberOfRooms={displayNumberOfRooms} currencyDisplayVariant={currencyDisplayVariant} />
        {showDisclaimerText && <Disclaimers isPriceLoading={isLoading} showCurrencyConversionText={Boolean(withCurrencyConversion)} showFeesPayableAtPropertyText={Boolean(withPropertyCurrencyConversion)} totalUpFront={totalUpFrontDisplayPrice} propertyCurrencyCode={propertyCurrencyCode} totalUpFrontChargeablePrice={totalUpFrontChargeablePrice} chargeableCurrencyCode={chargeableCurrencyCode} displayCurrencyCode={displayCurrencyCode} />}
      </PriceBreakdownWrapper>

      <LanguageCurrencySelector hasCurrency isMobile={isMobile} isOpen={currencySelectorModalOpen} onChangeCurrency={handleCurrencyChange} selectedCurrency={String(displayCurrencyCode)} onClose={() => setCurrencySelectorModalOpen(false)} availableCurrencies={enabledCurrencies} initialSelector="currency" />
    </>;
};
export default PriceDetails;

require("./index.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");